/**
 * Gestión → Fletes (`/gestion/fletes`).
 *
 * Puerto de `Compra::flete` + `views/menu/compra/flete.php`: los comprobantes del
 * transportista (`compra_flete`), con el importe, la detracción, el valor del
 * flete de las compras que agrupan y el detalle por línea (básico, C.U. de flete,
 * C.P.A., valor de compra, valor de flete y total C.P.A.), que en el panel
 * anterior vivía en un modal.
 *
 * Desde esta pantalla también se crean los fletes (eligiendo las compras que
 * agrupan y el costo de flete de cada línea), se corrigen sus precios, se anulan
 * y se administran los **proveedores de transporte**.
 */

import { Ban, Eye, Info, Lock, Plus, RefreshCw, Search, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { FleteNuevoDialog, TransportistasDialog } from '../components/fletes-dialogos';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatNumber, cn } from '@/lib/utils';

import { useAnularFlete, useFlete, useFletes } from '../compras.api';
import { etiquetaEstado, fechaCorta, monto, tonoEstado } from '../gestion-formato';
import type { FiltrosCompras } from '../compras.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Detalle de un flete: sus compras y sus líneas con el C.P.A. */
function DetalleFlete({ id, onClose }: { id: number | null; onClose: () => void }) {
  const consulta = useFlete(id);
  const datos = consulta.data;

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={datos ? `Flete ${datos.flete.comprobante}` : 'Flete'}
      description={
        datos
          ? `${datos.flete.proveedor ?? ''} · ${fechaCorta(datos.flete.fecha_emision)}`
          : 'Detalle del comprobante'
      }
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo abrir el flete">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {datos ? (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-4">
            {[
              ['Transportista', `${datos.flete.proveedor ?? '—'} (${datos.flete.ruc ?? '—'})`],
              ['Importe del comprobante', formatCurrency(datos.flete.importe_total ?? 0)],
              [
                'Detracción',
                datos.flete.detraccion
                  ? formatCurrency(datos.flete.monto_detraccion ?? 0)
                  : 'No aplica',
              ],
              ['IGV', datos.flete.exonerado_igv ? 'Exonerado' : 'Afecto'],
              ['Valor de compra', formatCurrency(datos.totales.valor_compra)],
              ['Valor de flete', formatCurrency(datos.totales.valor_flete)],
              ['Total C.P.A.', formatCurrency(datos.totales.total_cpa)],
              ['Estado', etiquetaEstado(datos.flete.estado)],
            ].map(([etiqueta, valor]) => (
              <div key={etiqueta}>
                <dt className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {etiqueta}
                </dt>
                <dd className="text-sm text-ink-800">{valor}</dd>
              </div>
            ))}
          </div>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">
              Compras agrupadas ({datos.compras.length})
            </h3>
            {datos.compras.length === 0 ? (
              <p className="text-sm text-ink-500">Este flete no tiene compras asociadas.</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {datos.compras.map((compra) => (
                  <li
                    key={compra.id}
                    className="rounded-control border border-ink-100 bg-white px-3 py-2 text-sm"
                  >
                    <span className="font-semibold text-ink-900">{compra.comprobante}</span>
                    <span className="ml-2 text-xs text-ink-500">
                      {fechaCorta(compra.fecha_emision)} · {formatCurrency(compra.importe_total ?? 0)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">
              Detalle por producto ({datos.lineas.length})
            </h3>
            <div className="max-h-[40vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2 whitespace-nowrap">Producto</th>
                    <th className="px-3 py-2 whitespace-nowrap">Comprobante</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Básico</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">C.U. flete</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">C.P.A.</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">V. compra</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">V. flete</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Total C.P.A.</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.lineas.map((linea) => (
                    <tr key={linea.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">{linea.producto}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-600">
                        {linea.comprobante}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatNumber(linea.cantidad)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {linea.basico.toLocaleString('es-PE', { maximumFractionDigits: 6 })}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {linea.precio_flete.toLocaleString('es-PE', { maximumFractionDigits: 6 })}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {linea.cpa.toLocaleString('es-PE', { maximumFractionDigits: 6 })}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatCurrency(linea.valor_compra)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatCurrency(linea.valor_flete)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                        {formatCurrency(linea.total_cpa)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}

export function FletesPage() {
  useDocumentTitle('Gestión · Fletes');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [estado, setEstado] = useState('1');
  const [serie, setSerie] = useState('');
  const [numero, setNumero] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [detalle, setDetalle] = useState<number | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [transportistas, setTransportistas] = useState(false);
  const [porAnular, setPorAnular] = useState<{ id: number; comprobante: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const anular = useAnularFlete();

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const filtros = useMemo<FiltrosCompras>(
    () => ({
      desde,
      hasta,
      estado,
      serie,
      numero,
      search: busqueda.trim(),
      orden: 'fecha',
      dir: 'desc',
      limit: 100,
      offset: 0,
    }),
    [desde, hasta, estado, serie, numero, busqueda],
  );

  const consulta = useFletes(filtros);
  const fletes = consulta.data?.fletes ?? [];
  const resumen = consulta.data?.resumen;

  return (
    <div className="space-y-5">
      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Fletes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Comprobantes del transportista y el valor de flete de las compras que agrupan.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Truck className="size-4" />}
            onClick={() => setTransportistas(true)}
          >
            Transportistas
          </Button>
          <Button size="sm" leftIcon={<Plus className="size-4" />} onClick={() => setNuevo(true)}>
            Nuevo flete
          </Button>
        </div>
      </header>

      {resumen ? (
        <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { etiqueta: 'Fletes', valor: String(resumen.total), detalle: `${resumen.activos} activos · ${resumen.anulados} anulados` },
            { etiqueta: 'Importe total', valor: formatCurrency(resumen.importe), detalle: 'Comprobantes del transportista' },
            { etiqueta: 'Con detracción', valor: String(resumen.con_detraccion), detalle: formatCurrency(resumen.detraccion) },
            { etiqueta: 'Mostrados', valor: String(fletes.length), detalle: 'Con los filtros actuales' },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Desde
              <CampoFecha
               
                className={CONTROL}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Hasta
              <CampoFecha
               
                className={CONTROL}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Estado
              <span className="relative block">
              <Select
                className={CONTROL}
                value={estado}
                onChange={(evento) => setEstado(evento.target.value)}
              >
                <option value="1">Activos</option>
                <option value="2">Anulados</option>
                <option value="todos">Todos</option>
              </Select>
                        </span>
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Buscar
              <span className="relative block">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                className={cn(CONTROL, 'pl-9')}
                placeholder="Transportista, RUC, comprobante…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
                        </span>
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Serie
              <input
                className={CONTROL}
                placeholder="D112"
                value={serie}
                onChange={(evento) => setSerie(evento.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Número
              <input
                className={CONTROL}
                placeholder="123"
                value={numero}
                onChange={(evento) => setNumero(evento.target.value)}
              />
            </label>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron cargar los fletes">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <p className="text-right text-xs text-ink-500">Importes en soles (S/)</p>

          <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Comprobante</th>
                  <th className="px-2 py-2">Transportista</th>
                  <th className="px-2 py-2">Emisión / vence</th>
                  <th className="px-2 py-2 text-right">Importe / flete</th>
                  <th className="px-2 py-2 text-center">Compras</th>
                  <th className="px-2 py-2 text-right">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 4 }).map((_, fila) => (
                      <tr key={`esqueleto-${fila}`} className="border-t border-ink-100">
                        {Array.from({ length: 6 }).map((__, columna) => (
                          <td key={`celda-${columna}`} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : null}

                {!consulta.isLoading && fletes.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-ink-500">
                      No hay fletes en el rango y filtros elegidos.
                    </td>
                  </tr>
                ) : null}

                {fletes.map((flete) => (
                  <tr key={flete.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-2">
                        <Truck className="size-4 text-brand-500" aria-hidden="true" />
                        <p className="font-semibold text-ink-900">{flete.comprobante}</p>
                        <Badge variant={tonoEstado(flete.estado)}>
                          {etiquetaEstado(flete.estado)}
                        </Badge>
                      </div>
                      <p className="text-xs text-ink-500">{flete.tipo_comprobante ?? '—'}</p>
                    </td>
                    <td className="max-w-[14rem] px-2 py-2">
                      <p className="truncate text-ink-800" title={flete.proveedor ?? ''}>
                        {flete.proveedor ?? '—'}
                      </p>
                      <p className="text-xs text-ink-500">{flete.ruc ?? ''}</p>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <p>{fechaCorta(flete.fecha_emision)}</p>
                      <p className="text-xs text-ink-500">
                        vence {fechaCorta(flete.fecha_vencimiento)}
                      </p>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <p className="font-semibold">{monto(flete.importe_total ?? 0)}</p>
                      <p className="text-xs text-ink-500">
                        flete {monto(flete.valor_flete_total ?? 0)}
                      </p>
                      {flete.detraccion ? (
                        <p className="text-xs text-ink-500">
                          detracción {monto(flete.monto_detraccion ?? 0)}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{flete.compras}</td>
                    <td className="px-2 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          title="Ver el detalle del flete"
                          aria-label={`Ver el detalle del flete ${flete.comprobante}`}
                          onClick={() => setDetalle(flete.id)}
                        >
                          <Eye className="size-4" />
                        </Button>
                        {Number(flete.estado) === 1 ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Anular el flete"
                            aria-label={`Anular el flete ${flete.comprobante}`}
                            onClick={() => setPorAnular({ id: flete.id, comprobante: flete.comprobante })}
                          >
                            <Ban className="size-4 text-rose-600" />
                          </Button>
                        ) : (
                          <Badge variant="neutral">ANULADO</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Alert variant="info" title="Cómo se reparte el flete">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Truck className="size-3.5" aria-hidden="true" />
              El flete agrupa las compras <strong>sin flete</strong> y reparte su costo entre las
              líneas: cada una guarda su precio de flete y su <strong>C.P.A.</strong>
              (básico + flete).
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              Anular un flete libera sus compras para volver a fletarlas; los precios de flete ya
              repartidos se conservan, como en el panel anterior.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El C.P.A. de cada línea es el básico más el costo unitario de flete, como en el modal del
        panel anterior.
      </p>

      <DetalleFlete id={detalle} onClose={() => setDetalle(null)} />

      <FleteNuevoDialog abierto={nuevo} onClose={() => setNuevo(false)} onAviso={mostrar} />

      <TransportistasDialog
        abierto={transportistas}
        onClose={() => setTransportistas(false)}
        onAviso={mostrar}
      />

      <ConfirmDialog
        open={porAnular !== null}
        title={`¿Anular el flete ${porAnular?.comprobante ?? ''}?`}
        description="Sus compras quedarán libres para volver a fletarlas. Los precios de flete ya repartidos no se deshacen."
        confirmLabel="Anular flete"
        tone="danger"
        loading={anular.isPending}
        onClose={() => setPorAnular(null)}
        onConfirm={() => {
          const flete = porAnular;

          setPorAnular(null);

          if (!flete) return;

          anular.mutate(flete.id, {
            onSuccess: (respuesta) => mostrar(respuesta.mensaje),
            onError: (fallo) =>
              mostrar(fallo instanceof ApiError ? fallo.message : 'No se pudo anular el flete.'),
          });
        }}
      />
    </div>
  );
}
